'use client';

import React from 'react';
import { NodeData } from '@/lib/specmap/types';

export interface NodeBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ConnectorsProps {
  nodes: NodeData[];
  selectedNodeId: string;
  hoveredNodeId: string | null;
  nodeBounds: Record<string, NodeBounds>;
}

const LANE_COLOR: Record<string, string> = {
  user: 'var(--teal)',
  admin: 'var(--blue)',
  support: 'var(--amber)',
  external: 'var(--purple)',
};

function connectorPath(from: NodeBounds, to: NodeBounds) {
  const startRight = from.x <= to.x;
  const startX = startRight ? from.x + from.width : from.x;
  const startY = from.y + from.height / 2;
  const endX = startRight ? to.x : to.x + to.width;
  const endY = to.y + to.height / 2;
  const controlDistance = Math.max(48, Math.abs(endX - startX) / 2);
  const c1 = startRight ? startX + controlDistance : startX - controlDistance;
  const c2 = startRight ? endX - controlDistance : endX + controlDistance;

  return `M ${startX} ${startY} C ${c1} ${startY}, ${c2} ${endY}, ${endX} ${endY}`;
}

export default function SpecMapConnectors({
  nodes,
  selectedNodeId,
  hoveredNodeId,
  nodeBounds,
}: ConnectorsProps) {
  const activeId = hoveredNodeId || selectedNodeId;
  const visibleIds = new Set(nodes.map((node) => node.id));
  const connectors = nodes.flatMap((node) =>
    node.dependencies
      .filter((dependency) => dependency.targetNodeId && visibleIds.has(dependency.targetNodeId))
      .map((dependency) => ({
        id: dependency.id,
        from: node.id,
        to: dependency.targetNodeId as string,
        status: dependency.status,
        lane: node.lane,
      }))
  );

  return (
    <svg data-component-id="src/app/components/SpecMapConnectors.tsx:61:4" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="61" data-component-end-line="109" data-component-file="SpecMapConnectors.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22absolute%20inset-0%20pointer-events-none%20z-10%22%7D"
      className="absolute inset-0 pointer-events-none z-10"
      style={{ minWidth: '1540px', minHeight: '900px' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs data-component-id="src/app/components/SpecMapConnectors.tsx:66:6" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="66" data-component-end-line="80" data-component-file="SpecMapConnectors.tsx" data-component-name="defs" data-component-content="%7B%22elementName%22%3A%22defs%22%7D">
        <marker data-component-id="src/app/components/SpecMapConnectors.tsx:67:8" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="67" data-component-end-line="69" data-component-file="SpecMapConnectors.tsx" data-component-name="marker" data-component-content="%7B%22elementName%22%3A%22marker%22%2C%22id%22%3A%22arrow-active%22%7D" id="arrow-active" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path data-component-id="src/app/components/SpecMapConnectors.tsx:68:10" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="68" data-component-end-line="68" data-component-file="SpecMapConnectors.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M0,0 L0,6 L6,3 z" fill="var(--teal)" />
        </marker>
        <marker data-component-id="src/app/components/SpecMapConnectors.tsx:70:8" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="70" data-component-end-line="72" data-component-file="SpecMapConnectors.tsx" data-component-name="marker" data-component-content="%7B%22elementName%22%3A%22marker%22%2C%22id%22%3A%22arrow-muted%22%7D" id="arrow-muted" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path data-component-id="src/app/components/SpecMapConnectors.tsx:71:10" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="71" data-component-end-line="71" data-component-file="SpecMapConnectors.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M0,0 L0,6 L6,3 z" fill="var(--border)" />
        </marker>
        <filter data-component-id="src/app/components/SpecMapConnectors.tsx:73:8" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="73" data-component-end-line="79" data-component-file="SpecMapConnectors.tsx" data-component-name="filter" data-component-content="%7B%22elementName%22%3A%22filter%22%2C%22id%22%3A%22connector-glow%22%7D" id="connector-glow">
          <feGaussianBlur data-component-id="src/app/components/SpecMapConnectors.tsx:74:10" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="74" data-component-end-line="74" data-component-file="SpecMapConnectors.tsx" data-component-name="feGaussianBlur" data-component-content="%7B%22elementName%22%3A%22feGaussianBlur%22%7D" stdDeviation="2" result="coloredBlur" />
          <feMerge data-component-id="src/app/components/SpecMapConnectors.tsx:75:10" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="75" data-component-end-line="78" data-component-file="SpecMapConnectors.tsx" data-component-name="feMerge" data-component-content="%7B%22elementName%22%3A%22feMerge%22%7D">
            <feMergeNode data-component-id="src/app/components/SpecMapConnectors.tsx:76:12" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="76" data-component-end-line="76" data-component-file="SpecMapConnectors.tsx" data-component-name="feMergeNode" data-component-content="%7B%22elementName%22%3A%22feMergeNode%22%7D" in="coloredBlur" />
            <feMergeNode data-component-id="src/app/components/SpecMapConnectors.tsx:77:12" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="77" data-component-end-line="77" data-component-file="SpecMapConnectors.tsx" data-component-name="feMergeNode" data-component-content="%7B%22elementName%22%3A%22feMergeNode%22%7D" in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {connectors.map((connector) => {
        const from = nodeBounds[connector.from];
        const to = nodeBounds[connector.to];

        if (!from || !to) {
          return null;
        }

        const active = connector.from === activeId || connector.to === activeId;
        const stroke = active ? LANE_COLOR[connector.lane] ?? 'var(--teal)' : 'var(--border)';
        const dashed = connector.status !== 'Complete' && connector.status !== 'Approved';

        return (
          <path data-component-id="src/app/components/SpecMapConnectors.tsx:95:10" data-component-path="src/app/components/SpecMapConnectors.tsx" data-component-line="95" data-component-end-line="106" data-component-file="SpecMapConnectors.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
            key={connector.id}
            d={connectorPath(from, to)}
            stroke={stroke}
            strokeWidth={active ? 2 : 1.25}
            fill="none"
            opacity={active ? 1 : 0.48}
            strokeDasharray={dashed ? '5 4' : 'none'}
            markerEnd={active ? 'url(#arrow-active)' : 'url(#arrow-muted)'}
            filter={active ? 'url(#connector-glow)' : undefined}
            style={{ transition: 'stroke 0.2s, opacity 0.2s, stroke-width 0.2s' }}
          />
        );
      })}
    </svg>
  );
}
